import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout/DashboardLayout";
import { formatDate } from "../../i18n/format";
import { getApiErrorMessage } from "../../lib/apiError";
import type { BandEnum, PublicReadinessQuestion, ReadinessAssessment } from "../../services/readiness/generated/model";
import {
  getReadinessStatusRetrieveQueryKey,
  useReadinessQuestionsList,
  useReadinessStatusRetrieve,
  useReadinessSubmitCreate,
} from "../../services/readiness/generated/readiness/readiness";
import "./ReadinessPage.css";

interface StatusPayload {
  hasAssessment: boolean;
  gateActive: boolean;
  score: number | null;
  band: BandEnum | null;
  passed: boolean;
  minScore?: number;
  completedAt?: string;
  answers?: ReadinessAssessment["answers"];
}

const bandClass = (band: BandEnum | null | undefined): string => {
  switch (band) {
    case "ready":
      return "readiness__band readiness__band--ready";
    case "developing":
      return "readiness__band readiness__band--developing";
    case "not_ready":
      return "readiness__band readiness__band--not-ready";
    default:
      return "readiness__band readiness__band--none";
  }
};

/**
 * WP2.1 — Export-readiness check. Companies answer a short list of
 * weighted multiple-choice questions; passing unlocks live match requests.
 * The questions and the pass mark are managed by moderators (see
 * `/moderator/readiness`). When no question is active the gate is off and
 * the page explains that.
 */
const ReadinessPage = () => {
  const { t } = useTranslation(["b2b", "common"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: questions = [], isLoading: questionsLoading, error: questionsError } = useReadinessQuestionsList();
  const { data: statusData, isLoading: statusLoading, error: statusError } = useReadinessStatusRetrieve();

  const status = statusData as StatusPayload | undefined;

  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [latest, setLatest] = useState<ReadinessAssessment | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const submitMutation = useReadinessSubmitCreate({
    mutation: {
      onSuccess: (data) => {
        setLatest(data as ReadinessAssessment);
        setAnswers({});
        setShowForm(false);
        setSubmitError(null);
        queryClient.invalidateQueries({ queryKey: getReadinessStatusRetrieveQueryKey() });
      },
      onError: (err) => {
        setSubmitError(getApiErrorMessage(err, t("b2b:readiness.errors.submit")));
      },
    },
  });

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    setSubmitError(null);
    const missing = questions.find((q: PublicReadinessQuestion) => answers[q.id] == null);
    if (missing) {
      setSubmitError(t("b2b:readiness.form.needAllAnswers"));
      return;
    }
    submitMutation.mutate({
      data: {
        answers: questions.map((q: PublicReadinessQuestion) => ({
          questionId: q.id,
          optionId: answers[q.id],
        })),
      },
    });
  };

  const bandLabel = (band: BandEnum | null | undefined): string => {
    switch (band) {
      case "ready":
        return t("b2b:readiness.bands.ready");
      case "developing":
        return t("b2b:readiness.bands.developing");
      case "not_ready":
        return t("b2b:readiness.bands.not_ready");
      default:
        return t("b2b:readiness.bands.none");
    }
  };

  const result =
    latest ??
    (status?.hasAssessment
      ? ({
          id: 0,
          score: status.score ?? 0,
          band: status.band ?? "not_ready",
          passed: status.passed,
          minScore: status.minScore ?? 0,
          completedAt: status.completedAt ?? "",
          answers: status.answers ?? [],
        } as unknown as ReadinessAssessment)
      : null);

  const gateActive = status?.gateActive ?? questions.length > 0;

  const renderStatusCard = () => {
    if (!result) {
      return (
        <section className="readiness__card">
          <h2 className="readiness__card-title">{t("b2b:readiness.currentStatus.title")}</h2>
          <p className="readiness__muted">{t("b2b:readiness.currentStatus.noAssessment")}</p>
        </section>
      );
    }
    return (
      <section className="readiness__card">
        <h2 className="readiness__card-title">{t("b2b:readiness.currentStatus.title")}</h2>
        <dl className="readiness__dl">
          <div>
            <dt>{t("b2b:readiness.currentStatus.score")}</dt>
            <dd>
              {result.score} / 100 ·{" "}
              <span className="readiness__muted">
                {t("b2b:readiness.currentStatus.minScore", { score: result.minScore })}
              </span>
            </dd>
          </div>
          <div>
            <dt>{t("b2b:readiness.currentStatus.band")}</dt>
            <dd>
              <span className={bandClass(result.band)}>{bandLabel(result.band)}</span>
            </dd>
          </div>
          <div>
            <dt>{t("b2b:readiness.currentStatus.status")}</dt>
            <dd>
              {result.passed ? t("b2b:readiness.currentStatus.passed") : t("b2b:readiness.currentStatus.notPassed")}
            </dd>
          </div>
          {result.completedAt ? (
            <div>
              <dt>{t("b2b:readiness.currentStatus.title")}</dt>
              <dd>{t("b2b:readiness.currentStatus.takenAt", { date: formatDate(result.completedAt) })}</dd>
            </div>
          ) : null}
        </dl>
        {gateActive ? (
          <button type="button" className="readiness__btn" onClick={() => setShowForm(true)}>
            {t("b2b:readiness.currentStatus.retake")}
          </button>
        ) : null}
      </section>
    );
  };

  const renderResultDetail = () => {
    if (!latest) return null;
    const tips = latest.answers.filter((a) => a.points < a.maxPoints && a.improvementTip); // i18n-ignore
    return (
      <section className="readiness__card">
        <h2 className="readiness__card-title">{t("b2b:readiness.result.title")}</h2>
        <p className="readiness__score">{t("b2b:readiness.result.scoreOf", { score: latest.score })}</p>
        <p>{latest.passed ? t("b2b:readiness.result.passedLead") : t("b2b:readiness.result.notPassedLead")}</p>
        {tips.length > 0 ? (
          <>
            <h3 className="readiness__card-subtitle">{t("b2b:readiness.result.improvementTips")}</h3>
            <ul className="readiness__tips">
              {tips.map((a) => (
                <li key={a.id}>
                  <strong>{a.questionText}</strong>
                  <br />
                  <span className="readiness__muted">{a.improvementTip}</span>
                </li>
              ))}
            </ul>
          </>
        ) : null}
        <div className="readiness__actions">
          {latest.passed ? (
            <button
              type="button"
              className="readiness__btn readiness__btn--primary"
              onClick={() => navigate("/b2b/requests")}
            >
              {t("b2b:readiness.result.goToRequests")}
            </button>
          ) : null}
          <button type="button" className="readiness__btn" onClick={() => setShowForm(true)}>
            {t("b2b:readiness.result.retake")}
          </button>
        </div>
      </section>
    );
  };

  const renderForm = () => {
    if (!gateActive) {
      return (
        <section className="readiness__card readiness__card--info">
          <p>{t("b2b:readiness.gateOff")}</p>
        </section>
      );
    }
    if (!showForm && result) return renderResultDetail();
    if (!showForm) {
      return (
        <section className="readiness__card">
          <p>{t("b2b:readiness.subtitle")}</p>
          <button type="button" className="readiness__btn readiness__btn--primary" onClick={() => setShowForm(true)}>
            {t("b2b:readiness.form.submit")}
          </button>
        </section>
      );
    }
    return (
      <form className="readiness__form" onSubmit={handleSubmit}>
        {questions.map((q: PublicReadinessQuestion, idx: number) => (
          <fieldset key={q.id} className="readiness__question">
            <legend className="readiness__question-title">
              <span className="readiness__progress">
                {t("b2b:readiness.form.progress", { current: idx + 1, total: questions.length })}
              </span>
              <span>{q.text}</span>
            </legend>
            {q.helpText ? <p className="readiness__help">{q.helpText}</p> : null}
            <ul className="readiness__options" role="list">
              {q.options.map((opt) => (
                <li key={opt.id}>
                  <label className="readiness__option">
                    <input
                      type="radio"
                      name={`q-${q.id}`}
                      value={opt.id}
                      checked={answers[q.id] === opt.id}
                      onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: opt.id }))}
                    />
                    <span>{opt.text}</span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        ))}
        {submitError ? <p className="readiness__error">{submitError}</p> : null}
        <div className="readiness__actions">
          <button type="submit" className="readiness__btn readiness__btn--primary" disabled={submitMutation.isPending}>
            {submitMutation.isPending ? t("b2b:readiness.form.submitting") : t("b2b:readiness.form.submit")}
          </button>
        </div>
      </form>
    );
  };

  const loading = questionsLoading || statusLoading;
  const loadError = questionsError || statusError;

  return (
    <DashboardLayout>
      <div className="readiness">
        <header className="readiness__header">
          <h1 className="readiness__title">{t("b2b:readiness.title")}</h1>
          <p className="readiness__muted">{t("b2b:readiness.subtitle")}</p>
        </header>
        {loading ? (
          <p className="readiness__muted">{t("common:states.loading")}</p>
        ) : loadError ? (
          <p className="readiness__error">{t("b2b:readiness.errors.loadQuestions")}</p>
        ) : (
          <>
            {renderStatusCard()}
            {renderForm()}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReadinessPage;
